import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "./contexts/ThemeContext";
import { FileProvider } from "./contexts/FileContext";
import { HelmetProvider } from "react-helmet-async";
import { ImageProvider } from "./contexts/ImageContext";
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";
import Analytics from "./components/common/Analytics";
import LoadingSpinner from "./components/ui/LoadingSpinner";
import "./App.css";
const ImageResizerGuide = lazy(() =>
  import("./pages/guides/ImageResizerGuide")
);
const WordCounterGuide = lazy(() => import("./pages/guides/WordCounterGuide"));
const TranslatorGuide = lazy(() => import("./pages/guides/TranslatorGuide"));
const TextSummarizerGuide = lazy(() =>
  import("./pages/guides/TextSummarizerGuide")
);
const WordConverterGuide = lazy(() =>
  import("./pages/guides/WordConverterGuide")
);
const TextReaderGuide = lazy(() => import("./pages/guides/TextReaderGuide"));
const TextConversionGuide = lazy(() =>
  import("./pages/guides/TextConversionGuide")
);
const SVGConverterGuide = lazy(() =>
  import("./pages/guides/SVGConverterGuide")
);

const PPTConversionGuide = lazy(() =>
  import("./pages/guides/PPTConversionGuide")
);
const PNGConversionGuide = lazy(() =>
  import("./pages/guides/PNGConversionGuide")
);

const PDFConversionGuide = lazy(() =>
  import("./pages/guides/PDFConversionGuide")
);

const OCRGuide = lazy(() => import("./pages/guides/OCRGuide"));
const JsonConversionGuide = lazy(() =>
  import("./pages/guides/JsonConversionGuide")
);
const JPGConverterGuide = lazy(() =>
  import("./pages/guides/JPGConverterGuide")
);
const HTMLConversionGuide = lazy(() =>
  import("./pages/guides/HTMLConversionGuide")
);
const ExcelConverterGuide = lazy(() =>
  import("./pages/guides/ExcelConverterGuide")
);
const PDFResizeGuide = lazy(() => import("./pages/guides/PDFResizeGuide"));
// guides pages
const PdfToWordGuide = lazy(() => import("./pages/guides/PdfToWordGuide"));
const ImageFormatGuide = lazy(() => import("./pages/guides/ImageFormatGuide"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));

// Lazy-loaded components
const SEO = lazy(() => import("./components/common/SEO"));
const Breadcrumbs = lazy(() => import("./components/Breadcrumbs"));
const StructuredData = lazy(() => import("./components/StructuredData"));

// Lazy-loaded pages
const HomePage = lazy(() => import("./pages/HomePage"));
const ImageToolsPage = lazy(() => import("./pages/tools/ImageToolsPage"));
const PdfToolsPage = lazy(() => import("./pages/tools/PdfToolsPage"));
const TextReaderPage = lazy(() => import("./pages/tools/TextReaderPage"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/legal/CookiePolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const ExcelPage = lazy(() => import("./pages/Converters/ExcelPage"));
const TextExtractorPage = lazy(() => import("./pages/tools/TextExtractorPage"));
const DMCAPage = lazy(() => import("./pages/DMCAPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const PDFPage = lazy(() => import("./pages/Converters/PDFPage"));
const JPGPage = lazy(() => import("./pages/Converters/JPGPage"));
const OfficePage = lazy(() => import("./pages/tools/OfficePage"));
const ConverterTools = lazy(() => import("./pages/tools/ConverterTools"));
const WordPage = lazy(() => import("./pages/Converters/WordPage"));
const PNGPage = lazy(() => import("./pages/Converters/PNGPage"));
const TextPage = lazy(() => import("./pages/Converters/TextPage"));
const SVGPage = lazy(() => import("./pages/Converters/SVGPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const FeaturesPage = lazy(() => import("./pages/FeaturesPage"));
const JSONPage = lazy(() => import("./pages/Converters/JSONPage"));
const HTMLPage = lazy(() => import("./pages/Converters/HTMLPage"));
const CounterPage = lazy(() => import("./pages/tools/CounterPage"));
const SummarizePage = lazy(() => import("./pages/tools/SummarizePage"));
const OCRCPage = lazy(() => import("./pages/Converters/OCRCPage"));
const TranslatePage = lazy(() => import("./pages/tools/TranslatePage"));
const PasswordPage = lazy(() => import("./pages/tools/PasswordPage"));
const PPTPage = lazy(() => import("./pages/tools/PPTPage"));
const SitemapPage = lazy(() => import("./pages/SitemapPage"));
// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-[60vh]">
    <LoadingSpinner />
  </div>
);

function App() {
  return (
    <Router>
      <HelmetProvider>
        <ThemeProvider>
          <FileProvider>
            <ImageProvider>
              <Analytics />
              <Suspense fallback={null}>
                <SEO />
                <StructuredData />
              </Suspense>
              <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-900">
                <Header />
                <main className="flex-grow container mx-auto px-4 py-8">
                  <Suspense fallback={null}>
                    <Breadcrumbs />
                  </Suspense>
                  <Routes>
                    {/* Home Page with Variants */}
                    <Route
                      path="/"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HomePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/online-file-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HomePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-free-file-conversion-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HomePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/file-converter-website"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HomePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-online-file-converter-tool-2023"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HomePage />
                        </Suspense>
                      }
                    />

                    {/* Image Tools Collection */}
                    <Route
                      path="/image-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/online-image-conversion-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-image-converter-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-image-format-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/photo-editing-and-conversion-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />

                    {/* Image Resizer */}
                    <Route
                      path="/image-resizer"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-online-image-resizer-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/resize-photos-for-social-media"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/change-image-dimensions-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/resize-images-for-website-free-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />

                    {/* JPG Converter */}

                    <Route
                      path="/jpg-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-online-image-converter-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/convert-to-jpg-online-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/jpg-image-converter-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-jpg-converter-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/high-quality-jpg-converter-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGPage />
                        </Suspense>
                      }
                    />

                    {/* PNG Converter */}
                    <Route
                      path="/png-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/convert-to-png-online-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/transparent-png-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/high-quality-png-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-png-converter-for-transparent-images"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGPage />
                        </Suspense>
                      }
                    />

                    {/* PDF Tools Collection */}
                    <Route
                      path="/pdf-toolkit"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-resizer"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/online-pdf-tools-collection"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-free-pdf-converter-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-editing-and-conversion-suite"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />

                    {/* PDF Compressor */}
                    <Route
                      path="/compress-pdf"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/reduce-pdf-file-size-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-size-reducer-free-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/shrink-pdf-for-email"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/how-to-reduce-pdf-size-for-email-attachment"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />

                    {/* Word Converter */}
                    <Route
                      path="/word-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/word-to-pdf-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/convert-word-to-pdf-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/doc-to-pdf-converter-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/microsoft-word-file-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-online-word-to-pdf-converter-no-watermark"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordPage />
                        </Suspense>
                      }
                    />

                    {/* Text Tools */}
                    <Route
                      path="/text-to-speech"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/online-text-to-voice-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-text-reader-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/convert-text-to-audio-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/read-aloud-text-tool-for-accessibility"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderPage />
                        </Suspense>
                      }
                    />

                    {/* Translation Tools */}
                    <Route
                      path="/translate"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-to-translate"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/online-text-translator-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/free-language-converter-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/multi-language-translation-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/translate-documents-to-english-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatePage />
                        </Suspense>
                      }
                    />

                    {/* Blog/Guide Pages */}
                    <Route
                      path="/blog/how-to-convert-pdf-to-word"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToWordGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-to-doc-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToWordGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-pdf-conversion-methods"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToWordGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/how-to-convert-pdf-to-word-without-losing-formatting"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToWordGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-to-word-conversion-tips-and-tricks"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToWordGuide />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/blog/image-format-comparison-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/jpg-vs-png-vs-svg-comparison"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-image-format-for-web"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/image-format-comparison-guide-for-social-media"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/choosing-the-right-image-format-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />

                    {/* Legal Pages */}
                    <Route
                      path="/privacy-policy"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PrivacyPolicy />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/terms-of-service"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TermsOfService />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/cookie-policy"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CookiePolicy />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/dmca"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <DMCAPage />
                        </Suspense>
                      }
                    />

                    {/* Info Pages */}
                    <Route
                      path="/contact-us"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ContactPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/about-us"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <AboutUs />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/faq"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <FAQPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/sitemap"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SitemapPage />
                        </Suspense>
                      }
                    />

                    {/* Additional Tools Pages */}
                    <Route
                      path="/excel-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ExcelPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/excel-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ExcelPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/excel-to-pdf-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ExcelPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-extractor"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextExtractorPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/login"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <LoginPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/signup"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SignupPage />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/pdf-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/universal-file-to-pdf-converter-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/office-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <OfficePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/converters"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ConverterTools />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/convert-Tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ConverterTools />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-to-pdf-converter-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/svg-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SVGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/svg-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SVGPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/features"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <FeaturesPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/json-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JSONPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/json-converters"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JSONPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/html-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HTMLPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/html-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HTMLPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/counter-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CounterPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/word-counter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <CounterPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-summarizer"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SummarizePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ocr-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <OCRCPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/password-generator"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PasswordPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ppt-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PPTPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ppt-converter"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PPTPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ppt-to-pdf-converter-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PPTPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/quikresize-tool-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <GuidesPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/tool-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <GuidesPage />
                        </Suspense>
                      }
                    />

                    {/* Guides */}
                    <Route
                      path="/pdf-resize-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFResizeGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/compress-pdf-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFResizeGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/pdf-resize-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFResizeGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/merge-pdf-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFResizeGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/pdf-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFResizeGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/excel-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ExcelConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/excel-converter-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ExcelConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/html-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HTMLConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/html-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <HTMLConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/jpg-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/jpg-converter-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JPGConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/json-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JsonConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/json-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <JsonConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ocr-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <OCRGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/ocr-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <OCRGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/png-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/png-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PNGConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/ppt-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PPTConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/ppt-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PPTConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/svg-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SVGConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/svg-converter-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <SVGConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/text-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextConversionGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/word-converter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/word-converter-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordConverterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-reader-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/text-reader-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextReaderGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/text-summarizer-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextSummarizerGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/text-summarizer-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TextSummarizerGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/translator-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatorGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/translator-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <TranslatorGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/word-counter-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordCounterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/word-counter-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <WordCounterGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/image-resizer-guides"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageResizerGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/image-resizer-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageResizerGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/image-format-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/image-resizing-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageResizerGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/guides/image-conversion-guide"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageResizerGuide />
                        </Suspense>
                      }
                    />

                    {/* Comparison Pages */}
                    <Route
                      path="/pdf-to-word-vs-pdf-to-doc-conversion"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToWordGuide />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/jpg-vs-png-vs-webp-comparison-2023"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageFormatGuide />
                        </Suspense>
                      }
                    />

                    {/* Seasonal/Trending Pages */}
                    <Route
                      path="/back-to-school-file-conversion-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ConverterTools />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/year-end-document-conversion-solutions"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ConverterTools />
                        </Suspense>
                      }
                    />

                    {/* Location-Specific Pages */}

                    <Route
                      path="/file-to-pdf-converter-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/pdf-converter-online-free-usa"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PDFPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/best-image-converter-tool-europe"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/resize-image-online"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <ImageToolsPage />
                        </Suspense>
                      }
                    />

                    {/* Problem-Solving Pages */}
                    <Route
                      path="/convert-scanned-documents-to-editable-text"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <OCRCPage />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/pdf-tools"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/compress-pdf-online-free-tool-fast-secure"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/compress-pdf-online-free-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/resize-pdf-online-free-tool-easy-accurate"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/resize-pdf-online-free-tool"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="/merge-pdf-files-online-free"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />

                    <Route
                      path="/how-to-shrink-large-pdf-files"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <PdfToolsPage />
                        </Suspense>
                      }
                    />

                    {/* 404 Page */}
                    <Route
                      path="*"
                      element={
                        <Suspense fallback={<PageLoader />}>
                          <NotFound />
                        </Suspense>
                      }
                    />
                  </Routes>
                </main>
                <Footer />
              </div>
            </ImageProvider>
          </FileProvider>
        </ThemeProvider>
      </HelmetProvider>
    </Router>
  );
}

export default App;
